import { Observable } from 'rxjs'

import { BackendStrategy } from '../backend-strategy'
import { BaseModel } from '../../../models'
import { Utils } from '../../..'
import { ModelRequestStore } from '../model-request-store/model-request-store'
import { ModelInclude, ModelIncludeFactory } from '../model-include'

interface SearchCriteria { [key: string]: any }

export class DeduplicatedFindBackend implements BackendStrategy<BaseModel> {
  private _requestStoreKeys: Map<string, Set<string>> = new Map<string, Set<string>>()

  constructor(private _decoratedBackend: BackendStrategy<BaseModel>,
              private _modelRequestStore: ModelRequestStore<Observable<BaseModel[]>>,
              private _utils: Utils,
              private _modelIncludeFactory: ModelIncludeFactory) {
  }

  public findById(type: string, id: string, includes?: string[]): Observable<BaseModel> {
    const hashKey = this._getRequestStoreKey(type, { id: id })
    return this._findOrDelegateRequest('findById', type, hashKey, id, includes)
          .map((result) => result[0]) as Observable<BaseModel>
  }

  public findBy(type: string, searchCriteria: any, includes?: string[]): Observable<BaseModel[]> {
    const hashKey = this._getRequestStoreKey(type, searchCriteria)
    return this._findOrDelegateRequest('findBy', type, hashKey, searchCriteria, includes) as Observable<BaseModel[]>
  }

  public save(model: BaseModel | BaseModel[]): Observable<BaseModel[]> | Observable<BaseModel> {
    return this._delegateAndInvalidateRequests('save', model)
  }

  public delete(models: BaseModel[]): Observable<BaseModel[]> {
    return this._delegateAndInvalidateRequests('delete', models) as  Observable<BaseModel[]>
  }

  private _findOrDelegateRequest(method: string, type: string, hashKey: string,
                                 searchCriteria: { [key: string]: any } | string,
                                 includes: string[] = []): Observable<BaseModel|BaseModel[]> {

    const includesModel = this._modelIncludeFactory.fromIncludes(includes)
    let request = this._modelRequestStore.getRequest(hashKey, includesModel)
    if (request) {
      return request
    }

    request = this._decoratedBackend[method](type, searchCriteria, includes).share()
    this._modelRequestStore.setRequest(hashKey, includesModel, request)
    return request.map((result) => Array.isArray(result) ? result : [result])
                  .do(() => {
                    this._modelRequestStore.deleteRequest(hashKey, includesModel)
                    const keyStore = this._requestStoreKeys.get(type) || new Set()
                    keyStore.delete(hashKey)
                  })
  }

  private _delegateAndInvalidateRequests(method: string, models: BaseModel | BaseModel[]) {
    return this._decoratedBackend[method](models).do(() => {
      this._invalidateAllFindRequests(models)
    })
  }

  private _invalidateAllFindRequests(model: BaseModel | BaseModel[]) {
    const models = Array.isArray(model) ? model : [model]
    const uniqueTypes = models.reduce((result, mdl) => {
      if (!result.includes(mdl.type)) { result.push(mdl.type) }
      return result
    }, [])

    uniqueTypes.forEach((type) => {
      const usedKeys = this._requestStoreKeys.get(type) || new Set()
      usedKeys.forEach((key) => this._modelRequestStore.deleteRequest(key))
      usedKeys.clear()
      this._requestStoreKeys.delete(type)
    })
  }

  private _getRequestStoreKey(type: string, searchCriteria: { [key: string]: any }): string {
    const sortedSearchCriteria = this._utils.getSortedObject(searchCriteria)
    const searchCriteriaHash = this._utils.hashCode(JSON.stringify(sortedSearchCriteria))
    const typeHash = `${type}_${searchCriteriaHash}`

    const hashesByType = this._requestStoreKeys.get(type) || new Set()
    hashesByType.add(typeHash)
    this._requestStoreKeys.set(type, hashesByType)
    return typeHash
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/backends/deduplicated-find-backend/deduplicated-find-backend.ts